import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor para agregar el token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Una sola renovacion a la vez: si vencio el access, todos los pedidos en
// vuelo reciben 401 al mismo tiempo y sin esto se dispararia un refresh por cada
// uno.
let renovacionEnVuelo = null;

const renovarAcceso = async () => {
  renovacionEnVuelo ??= (async () => {
    const { refreshToken, setToken, logout } = useAuthStore.getState();
    if (!refreshToken) return null;
    try {
      // axios pelado y no `api`: este interceptor volveria a llamarse.
      const { data } = await axios.post(
        `${api.defaults.baseURL}/auth/refresh`,
        { refreshToken }
      );
      setToken(data.data.token);
      return data.data.token;
    } catch {
      logout();
      return null;
    } finally {
      renovacionEnVuelo = null;
    }
  })();
  return renovacionEnVuelo;
};

// Response interceptor: el access dura una hora, asi que un 401 casi siempre es
// "vencido" y no "no autorizado". Se renueva y se reintenta una vez; recien si
// el refresh tambien falla se manda al login.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && original && !original._reintentado) {
      original._reintentado = true;
      const nuevo = await renovarAcceso();
      if (nuevo) {
        original.headers.Authorization = `Bearer ${nuevo}`;
        return api(original);
      }
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  // Revoca el refresh en el server: sin esto la sesion cerrada sigue pudiendo
  // renovarse hasta que venza sola.
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
};

// Recolecciones API
export const recoleccionAPI = {
  verificarHabilitado: (data) => api.post('/recoleccion/habilitado', data),
  grabarPedido: (data) => api.post('/recoleccion/grabarpedido', data),
  anularPedido: (data) => api.post('/recoleccion/anularpedido', data),
  obtenerRecolecciones: (data) => api.post('/recoleccion/recolectar', data),
  obtenerRecolectados: (data) => api.post('/recoleccion/recolectados', data),
  cargarServicios: (data) => api.post('/recoleccion/cargarservicios', data),
  cargarZonas: (data) => api.post('/recoleccion/cargarzonas', data),
  cargarCausas: (data) => api.post('/recoleccion/cargarcausas', data),
  cargarMoviles: (data) => api.post('/recoleccion/cargarmoviles', data),
  grabarContribuyente: (data) => api.post('/recoleccion/grabarcontribuyente', data),
  checkEmailExists: (data) => api.post('/recoleccion/checkemailexists', data),
  ultimasRecolecciones: (data) => api.post('/recoleccion/ultimasrecolecciones', data),
};

// Reportes API
export const reportesAPI = {
  viajesDiariosEmail: (data) => api.post('/reportes/viajesdiariosmail', data),
};

// Móviles API
export const movilesAPI = {
  listar: () => api.get('/moviles'),
  obtener: (id) => api.get(`/moviles/${id}`),
  crear: (data) => api.post('/moviles', data),
  actualizar: (id, data) => api.put(`/moviles/${id}`, data),
  eliminar: (id) => api.delete(`/moviles/${id}`),
  verificarEliminacion: (id) => api.get(`/moviles/${id}/can-delete`),
};

// Servicios API
export const serviciosAPI = {
  listar: () => api.get('/servicios'),
  obtener: (id) => api.get(`/servicios/${id}`),
  crear: (data) => api.post('/servicios', data),
  actualizar: (id, data) => api.put(`/servicios/${id}`, data),
  eliminar: (id) => api.delete(`/servicios/${id}`),
  verificarEliminacion: (id) => api.get(`/servicios/${id}/can-delete`),
};

// Upload API
export const uploadAPI = {
  subirImagen: (file, organizacion) => {
    const formData = new FormData();
    formData.append('organizacion', organizacion);
    formData.append('foto', file);
    return api.post('/upload/foto', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Zonas API
// Geocoding API
export const geocodingAPI = {
  reverse: (lat, lng) => api.get('/geocoding/reverse', { params: { lat, lng } }),
};

export const zonasAPI = {
  listar: () => api.get('/zonas'),
  obtener: (id) => api.get(`/zonas/${id}`),
  obtenerLimites: (id) => api.get(`/zonas/${id}/limites`),
  crear: (data) => api.post('/zonas', data),
  actualizar: (id, data) => api.put(`/zonas/${id}`, data),
  guardarLimites: (id, limites) => api.put(`/zonas/${id}/limites`, { limites }),
  eliminar: (id) => api.delete(`/zonas/${id}`),
  verificarEliminacion: (id) => api.get(`/zonas/${id}/can-delete`),
  obtenerMensajes: (id) => api.get(`/zonas/${id}/mensajes`),
  enviarMensaje: (id, data) => api.post(`/zonas/${id}/mensajes`, data),
};

// Causas API
export const causasAPI = {
  listar: () => api.get('/causas'),
  obtener: (id) => api.get(`/causas/${id}`),
  crear: (data) => api.post('/causas', data),
  actualizar: (id, data) => api.put(`/causas/${id}`, data),
  eliminar: (id) => api.delete(`/causas/${id}`),
  verificarEliminacion: (id) => api.get(`/causas/${id}/can-delete`),
};

// Contribuyentes API
export const contribuyentesAPI = {
  listar: () => api.get('/contribuyentes'),
  buscar: (q) => api.get('/contribuyentes/buscar', { params: { q } }),
  obtener: (id) => api.get(`/contribuyentes/${id}`),
  obtenerRecolecciones: (id) => api.get(`/contribuyentes/${id}/recolecciones`),
  crear: (data) => api.post('/contribuyentes', data),
  actualizar: (id, data) => api.put(`/contribuyentes/${id}`, data),
  eliminar: (id) => api.delete(`/contribuyentes/${id}`),
  verificarEliminacion: (id) => api.get(`/contribuyentes/${id}/can-delete`),
  obtenerMensajes: (id) => api.get(`/contribuyentes/${id}/mensajes`),
  enviarMensaje: (id, data) => api.post(`/contribuyentes/${id}/mensajes`, data),
};

// Dashboard API
export const dashboardAPI = {
  resumen: () => api.get('/dashboard/resumen'),
  mapaHoy: () => api.get('/dashboard/mapa-hoy'),
};

// Gestión API
export const gestionAPI = {
  listarRecolecciones: (params) => api.get('/gestion/recolecciones', { params }),
  crearRecoleccion: (data) => api.post('/gestion/recolecciones', data),
  actualizarRecoleccion: (id, data) => api.put(`/gestion/recolecciones/${id}`, data),
  anularRecoleccion: (id) => api.put(`/gestion/recolecciones/${id}/anular`),
  zonasPorUbicacion: (lat, lng) => api.get('/gestion/ubicacion-zonas', { params: { lat, lng } }),
};

// Organización API
export const organizacionAPI = {
  obtener: () => api.get('/organizacion'),
  subirLogo: (file) => {
    const form = new FormData();
    form.append('logo', file);
    return api.post('/organizacion/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  eliminarLogo: () => api.delete('/organizacion/logo'),
};

// Usuarios API
export const usuariosAPI = {
  listar: () => api.get('/usuarios'),
  crear: (data) => api.post('/usuarios', data),
  actualizar: (id, data) => api.put(`/usuarios/${id}`, data),
  eliminar: (id) => api.delete(`/usuarios/${id}`),
};

// Itinerarios API
export const itinerariosAPI = {
  listar: () => api.get('/itinerarios'),
  obtener: (lote) => api.get(`/itinerarios/${lote}`),
  generar: (data) => api.post('/itinerarios/generar', data),
  eliminar: (lote) => api.delete(`/itinerarios/${lote}`),
};

// API Keys (solo admin, nivel 0)
export const apiKeysAPI = {
  listar: () => api.get('/api-keys'),
  crear: (data) => api.post('/api-keys', data),
  revocar: (id) => api.put(`/api-keys/${id}`),
  eliminar: (id) => api.delete(`/api-keys/${id}`),
};

// Direcciones del contribuyente (endpoints móvil, sin auth JWT requerida)
export const movilDireccionesAPI = {
  listarPorEmail: (email) => api.get('/movil/direcciones', { params: { email } }),
  agregar: (data) => api.post('/movil/direccion', data),
  actualizar: (id, data) => api.put(`/movil/direccion/${id}`, data),
  desactivar: (data) => api.post('/movil/direccion/desactivar', data),
};
